import sortBy from 'lodash/sortBy';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Store} from 'common/report/stores/store';
import {pluralize} from 'common/util/string';
import {SectionItem} from 'common/components/items-menu/types';
import {baseComparator} from 'common/util';

import type {PropertyItem} from './properties-menu/types';
import {CohortSegment} from './segment-menu/types';
import {TRACKING_EVENTS} from './constants';
import {EventItem} from './events-menu/types';

/**
 * Builds cohort definitions from a list of cohorts
 */
export function getCohortsDefinitions(cohorts) {
  return sortBy(
    (cohorts || []).map((cohort) => ({
      label: cohort.name,
      value: cohort.id,
      name: cohort.name,
      count: cohort.count,
      description: cohort.description,
      createdBy: cohort.created_by,
      lastEdited: cohort.last_edited,
      can_view: cohort.can_view,
      can_update_basic: cohort.can_update_basic,
    })),
    `label`,
  );
}

export function getCohortFromPropertyItem(item: PropertyItem): CohortSegment {
  let cohort = Number.isInteger(item.value) ? {id: item.value} : item.value.cohort;
  const isInlineCohort = cohort && !!cohort.raw_cohort;

  if (isInlineCohort) {
    cohort = {raw_cohort: {name: cohort.name || item.label, ...cohort.raw_cohort}};
  } else {
    cohort = {...(item.dataGroupId ? {data_group_id: item.dataGroupId} : {}), ...cohort};
  }

  return {cohort: {...cohort, name: item.label, negated: false}};
}

export function getEventMenuItemSublabel(item: SectionItem<EventItem>) {
  if (item.uiUniqueQueryCount > 0) {
    return {
      ...item,
      subLabel: `Used by ${item.uiUniqueQueryCount} ${pluralize(`teammate`, item.uiUniqueQueryCount)} recently`,
    };
  } else {
    return item;
  }
}

/**
 * Updates a given Entity type description
 * @param entityStore
 * @param newDescription
 * @param storeKey
 * @param updateRequestObj
 * @param undoRequestObj
 * @param entityType
 */
export async function updateEntityDescription(
  entityStore: Store<any>,
  newDescription,
  storeKey,
  updateRequestObj,
  undoRequestObj,
  entityType,
  track: (...args) => void,
) {
  const descriptionBeforeUpdate = entityStore.value[storeKey].description;

  const updateStoreWithDescription = (description: string) =>
    entityStore.update({
      [storeKey]: {
        ...entityStore.value[storeKey],
        description,
      },
    });

  const undoCallback = async () => {
    try {
      updateStoreWithDescription(descriptionBeforeUpdate);
      await entityStore.patch({
        ...undoRequestObj,
      });
    } catch (err) {
      addToast({message: `Unable to undo the description`, isError: true, type: ToastType.ERROR});
      updateStoreWithDescription(newDescription);
    }
  };

  track(
    TRACKING_EVENTS.EDIT_DESCRIPTION,
    {
      'New description': newDescription,
      'Old description': descriptionBeforeUpdate,
      'Access point': `context panel`,
      'Entity type': entityType,
    },
    false,
  );

  updateStoreWithDescription(newDescription);
  try {
    await entityStore.patch({
      ...updateRequestObj,
    });
    addToast({
      message: `'${entityStore.value[storeKey].name}' has been updated`,
      type: ToastType.SUCCESS,
      undoable: true,
      undoCallback,
    });
  } catch (err) {
    addToast({message: `Failed to update the description`, isError: true, type: ToastType.ERROR});
    updateStoreWithDescription(descriptionBeforeUpdate);
  }
}

export function sortItemsByLabel<T extends SectionItem>(items: T[], {order}: {order: `asc` | `desc`} = {order: `asc`}) {
  return items.sort(baseComparator({order, transform: (item: T) => item.label.toLowerCase()}));
}
